import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus, Send, Trash2, Pin, Clock, CheckCircle2, AlertTriangle, Users, Lock } from "lucide-react";

function TierBadge({ posted, scheduled, label, color }) {
  if (posted) {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${color}`}>
        <CheckCircle2 size={11} /> {label}
      </span>
    );
  }
  if (scheduled) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500">
        <Clock size={11} /> {label} pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-50 text-slate-400">
      {label} —
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

// Direction labels — Bullish / Bearish / Indecisive. Keeps the DB
// values as the legacy `long` / `short` strings (plus the new
// `neutral`) so existing docs render correctly.
const DIR_LABEL = { long: "BULLISH", short: "BEARISH", neutral: "INDECISIVE" };
function dirLabel(d) {
  return DIR_LABEL[(d || "long").toLowerCase()] || "BULLISH";
}
function dirIcon(d) {
  const dir = (d || "long").toLowerCase();
  if (dir === "short")   return <TrendingDown size={16} className="text-rose-600" />;
  if (dir === "neutral") return <Minus size={16} className="text-slate-500" />;
  return <TrendingUp size={16} className="text-emerald-600" />;
}

const R_STATUSES = ["target_hit", "stopped", "breakeven"];
const fmtR = (v) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v)}R`;

function ResultR({ row, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(row.result_r ?? "");
  useEffect(() => { setVal(row.result_r ?? ""); }, [row.result_r]);
  if (row.status === "expired") return <span data-testid={`admin-trade-setup-expired-${row.id}`} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">⌛ Expired</span>;
  if (row.status === "invalidated") return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500" data-testid={`admin-trade-setup-status-${row.id}`}>⏸ Invalidated</span>;
  if (!R_STATUSES.includes(row.status)) return null;
  const chip = { target_hit: "✅ Target hit", stopped: "❌ Stopped", breakeven: "➖ Breakeven" }[row.status];
  const save = async () => {
    try { await api.patch(`/admin/trade-setups/${row.id}/result`, { rr: String(val).trim() || null }); toast.success("R saved"); setEdit(false); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save R"); }
  };
  if (edit) return (
    <span className="inline-flex items-center gap-1">
      <Input data-testid={`admin-trade-setup-rr-input-${row.id}`} className="h-7 w-24 text-xs" value={val} onChange={(e) => setVal(e.target.value)} placeholder="+3.5" />
      <Button size="sm" className="h-7 text-xs" onClick={save} data-testid={`admin-trade-setup-rr-save-${row.id}`}>Save</Button>
    </span>
  );
  const r = row.result_r;
  const tone = r == null ? "bg-slate-100 text-slate-500" : r > 0 ? "bg-emerald-50 text-emerald-700" : r < 0 ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-700";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-xs font-semibold text-slate-600" data-testid={`admin-trade-setup-status-${row.id}`}>{chip}</span>
      <button onClick={() => setEdit(true)} data-testid={`admin-trade-setup-rr-${row.id}`} className={`text-xs font-bold px-2 py-0.5 rounded-full ${tone}`}>
        {r == null ? "Add R" : fmtR(r)}
      </button>
    </span>
  );
}

const CARD_LABELS = [["feed_en", "Feed EN"], ["feed_es", "Feed ES"], ["story_en", "Story EN"], ["story_es", "Story ES"]];

function CardLinks({ id, cards }) {
  if (!cards || !cards.feed_en) return null;
  const src = (u) => `${process.env.REACT_APP_BACKEND_URL}${new URL(u).pathname}`;
  return (
    <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid={`admin-trade-setup-cards-${id}`}>
      <a href={src(cards.feed_en)} target="_blank" rel="noreferrer"><img src={src(cards.feed_en)} alt="" className="h-28 rounded-md border border-slate-200" /></a>
      <div className="flex flex-wrap gap-1.5">
        {CARD_LABELS.filter(([k]) => cards[k]).map(([k, label]) => (
          <a key={k} href={src(cards[k])} download target="_blank" rel="noreferrer" data-testid={`admin-trade-setup-card-${k}-${id}`}
            className="text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100">{label}</a>
        ))}
      </div>
    </div>
  );
}

export default function AdminTradeSetups() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    symbol: "",
    direction: "long",
    entry: "",
    tp: "",
    sl: "",
    thesis: "",
    chart_url: "",
    zone: "",
    quote: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/trade-setups");
      setRows(r.data.setups || []);
    } catch (e) {
      toast.error("Couldn't load setups.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.symbol.trim()) return toast.error("Symbol is required.");
    setSaving(true);
    try {
      const r = await api.post("/admin/trade-setups", form);
      if (r.data.last_error) {
        toast.warning("Saved, but Discord post failed", { description: r.data.last_error });
      } else {
        toast.success("Setup posted to Premium — Basic drip scheduled");
      }
      setForm({ title: "", symbol: "", direction: "long", entry: "", tp: "", sl: "", thesis: "", chart_url: "", zone: "", quote: "" });
      load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const releaseFree = async (id) => {
    if (!window.confirm("Release this setup to the Free tier now?")) return;
    try {
      await api.post(`/admin/trade-setups/${id}/release-to-free`);
      toast.success("Released to Free");
      load();
    } catch (e) {
      toast.error("Release failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const repostBasicNow = async (id) => {
    if (!window.confirm("Post the Basic mirror immediately (skips the 36h delay)?")) return;
    try {
      await api.post(`/admin/trade-setups/${id}/repost-basic`);
      toast.success("Basic mirror posted");
      load();
    } catch (e) {
      toast.error("Post failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  // Update modal state — reused across every setup card. Storing the
  // full setup so the modal can show which tiers will receive the update.
  const [updateFor, setUpdateFor]     = useState(null);   // {setup} or null
  const [updateNote, setUpdateNote]   = useState("");
  const [updateStatus, setUpdateStatus] = useState("open");
  const [updateRR, setUpdateRR] = useState("");
  const [updateSending, setUpdateSending] = useState(false);

  const openUpdateModal = (setup) => {
    setUpdateFor(setup);
    setUpdateNote("");
    setUpdateStatus("open");
    setUpdateRR("");
  };
  const closeUpdateModal = () => {
    if (updateSending) return;
    setUpdateFor(null);
    setUpdateNote("");
    setUpdateStatus("open");
  };
  const submitUpdate = async () => {
    if (!updateFor || !updateNote.trim()) {
      toast.error("Type an update note first");
      return;
    }
    setUpdateSending(true);
    try {
      const r = await api.post(
        `/admin/trade-setups/${updateFor.id}/updates`,
        { note: updateNote.trim(), status: updateStatus, rr: R_STATUSES.includes(updateStatus) && updateRR.trim() ? updateRR.trim() : null },
      );
      const fanout = r.data.fanout || [];
      const counts = r.data.counts || {};
      // eslint-disable-next-line no-console
      console.log("[trade-setup update] result", r.data);

      const posted = fanout.filter((x) => x.ok);
      const skipped = fanout.filter((x) => x.skipped);
      const failed = fanout.filter((x) => !x.ok && !x.skipped);

      // Primary toast — posted threads.
      if (posted.length > 0) {
        toast.success(`Update posted to ${posted.length} thread${posted.length === 1 ? "" : "s"}`, {
          description: posted.map((x) => `• ${x.target} — ${x.link ? "jump →" : x.message_id}`).join("\n"),
          duration: 10000,
        });
      } else {
        toast.error("Update posted to 0 threads", {
          description: "Nothing landed in Discord — see breakdown below.",
          duration: 10000,
        });
      }
      // Explicit breakdown for skipped slots so admins know WHY the
      // premium (or any) thread is empty — the original message's
      // auto-thread probably failed at post time, so updates have
      // no thread to nest in.
      if (skipped.length > 0) {
        toast.warning(`${skipped.length} slot(s) skipped — no thread exists`, {
          description:
            skipped.map((x) => `• ${x.target}: no thread id stored`).join("\n") +
            "\n\nIf this is a tier that should already be released (e.g. Premium), the original Discord message was posted but the thread auto-spawn failed. Re-post the setup or manually create a thread on the original message.",
          duration: 15000,
        });
      }
      if (failed.length > 0) {
        toast.error(`${failed.length} thread post(s) failed`, {
          description: failed.map((x) => `• ${x.target}: ${x.error || "unknown"}`).join("\n"),
          duration: 15000,
        });
      }
      closeUpdateModal();
      load();
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setUpdateSending(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this setup row? (Discord messages already sent will remain.)")) return;
    try {
      await api.delete(`/admin/trade-setups/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const pinInfo = async () => {
    if (!window.confirm("Post + pin the 'How this channel works' info card in all 6 Market Outlook channels (3 EN + 3 ES)?")) return;
    try {
      const r = await api.post("/admin/trade-setups/pin-info");
      const entries = Object.entries(r.data);
      const okSlots = entries.filter(([_, v]) => v.ok);
      const failSlots = entries.filter(([_, v]) => !v.ok);

      // Dump the full diagnostic to the console so the admin can inspect
      // channel names, IDs, and jump-links if something looks off.
      // eslint-disable-next-line no-console
      console.log("[pin-info] result", r.data);

      toast.success(`Info card posted to ${okSlots.length} channel(s)`, {
        description: okSlots
          .map(([k, v]) => `${k} → #${v.channel_name || "?"}${v.pinned === false ? " (not pinned)" : ""}`)
          .join("\n"),
        duration: 12000,
      });

      if (failSlots.length > 0) {
        toast.warning(`${failSlots.length} channel(s) skipped`, {
          description: failSlots.map(([k, v]) => `${k}: ${v.error || "unknown"}`).join("\n"),
          duration: 12000,
        });
      }
    } catch (e) {
      toast.error("Pin failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const pinGuide = async () => {
    if (!window.confirm("Re-post the 3-part Market Outlook guide in the admin staging channel and pin it (older guide pins are removed)?")) return;
    try {
      const r = await api.post("/admin/trade-setups/pin-guide");
      if (r.data.ok) toast.success(`Guide posted and ${r.data.pinned}/3 pinned`);
      else toast.warning("Guide not fully posted", { description: r.data.error || `${r.data.pinned || 0}/3 pinned` });
    } catch (e) {
      toast.error("Guide post failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const syncPerms = async () => {
    if (!window.confirm(
      "This will replace the current role permissions on all 3 trade-setup channels:\n\n" +
      "• #trade-setups → Peak Premium only (read-only)\n" +
      "• #trade-setups-basic → Peak Basic only (read-only)\n" +
      "• #trade-setups-free → Peak Free only (read-only)\n\n" +
      "Any custom overrides on those channels will be wiped. Continue?"
    )) return;
    try {
      const r = await api.post("/admin/trade-setups/sync-channel-perms");
      const okTiers = Object.entries(r.data).filter(([_, v]) => v.ok).map(([k]) => k);
      const failTiers = Object.entries(r.data).filter(([_, v]) => !v.ok);
      if (failTiers.length === 0) {
        toast.success(`Permissions synced on all ${okTiers.length} channels`, {
          description: "Each tier now sees only their own channel.",
        });
      } else {
        const errs = failTiers.map(([k, v]) => `${k}: ${v.errors.join("; ")}`).join("\n");
        toast.warning(`${okTiers.length} ok, ${failTiers.length} failed`, { description: errs });
      }
    } catch (e) {
      toast.error("Sync failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div data-testid="admin-trade-setups" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-amber-600 font-semibold mb-1">— Admin</div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight">
            Trade Setups
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Premium sees setups in real-time. Basic sees them <strong>36h delayed</strong>.
            Free tier is a manual release — you pick which setups to share and when.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={syncPerms}
            variant="outline"
            className="gap-2"
            data-testid="admin-trade-setups-sync-perms-btn"
          >
            <Lock size={14} /> Sync channel permissions
          </Button>
          <Button
            onClick={pinInfo}
            variant="outline"
            className="gap-2"
            data-testid="admin-trade-setups-pin-info-btn"
          >
            <Pin size={14} /> Post + pin info card
          </Button>
          <Button onClick={pinGuide} variant="outline" className="gap-2" data-testid="admin-trade-setups-pin-guide-btn">
            <Pin size={14} /> Re-pin admin guide
          </Button>
        </div>
      </div>

      {/* Create form */}
      <Card className="p-6 border-slate-200">
        <div className="text-xs uppercase tracking-[0.24em] text-slate-500 font-semibold mb-4">— New setup</div>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Input
            data-testid="admin-trade-setups-symbol"
            placeholder="Symbol (e.g. EURUSD)"
            value={form.symbol}
            onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
            className="uppercase"
          />
          <select
            data-testid="admin-trade-setups-direction"
            value={form.direction}
            onChange={(e) => setForm({ ...form, direction: e.target.value })}
            className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
          >
            <option value="long">🟢 Bullish</option>
            <option value="short">🔴 Bearish</option>
            <option value="neutral">⚪ Indecisive</option>
          </select>
          <Input
            data-testid="admin-trade-setups-title"
            placeholder="Title (optional)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="sm:col-span-2"
          />
          <Input
            data-testid="admin-trade-setups-entry"
            placeholder="Entry"
            value={form.entry}
            onChange={(e) => setForm({ ...form, entry: e.target.value })}
          />
          <Input
            data-testid="admin-trade-setups-tp"
            placeholder="Take Profit"
            value={form.tp}
            onChange={(e) => setForm({ ...form, tp: e.target.value })}
          />
          <Input
            data-testid="admin-trade-setups-sl"
            placeholder="Stop Loss"
            value={form.sl}
            onChange={(e) => setForm({ ...form, sl: e.target.value })}
          />
          <Input
            data-testid="admin-trade-setups-zone"
            placeholder="Daily zone (e.g. 4185 – 4195)"
            value={form.zone}
            onChange={(e) => setForm({ ...form, zone: e.target.value })}
          />
          <Input
            data-testid="admin-trade-setups-quote"
            placeholder="Motivation line (blank = random, 'none' = hide)"
            value={form.quote}
            onChange={(e) => setForm({ ...form, quote: e.target.value })}
          />
          <Input
            data-testid="admin-trade-setups-chart-url"
            placeholder="Chart image URL (optional)"
            value={form.chart_url}
            onChange={(e) => setForm({ ...form, chart_url: e.target.value })}
          />
          <Textarea
            data-testid="admin-trade-setups-thesis"
            placeholder="Thesis — why this setup? What's the invalidation? (shown in the Discord embed)"
            value={form.thesis}
            onChange={(e) => setForm({ ...form, thesis: e.target.value })}
            className="sm:col-span-4 min-h-[100px]"
          />
          <div className="sm:col-span-4 flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              className="gap-2 bg-blue-800 hover:bg-blue-900"
              data-testid="admin-trade-setups-submit"
            >
              <Send size={14} /> {saving ? "Posting…" : "Post to Premium now"}
            </Button>
          </div>
        </form>
      </Card>

      {/* List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-slate-500 text-sm p-6">Loading setups…</div>
        ) : rows.length === 0 ? (
          <Card className="p-12 text-center text-slate-500 text-sm">
            No setups yet. The first one you post goes out to Premium immediately.
          </Card>
        ) : rows.map((r) => (
          <Card key={r.id} data-testid={`admin-trade-setup-row-${r.id}`} className="p-5 border-slate-200">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {dirIcon(r.direction)}
                  <div className="font-display font-black text-lg text-slate-900">
                    {r.title || `${r.symbol} · ${dirLabel(r.direction)}`}
                  </div>
                  <span className="text-xs text-slate-400">· {fmtDate(r.created_at)}</span>
                  <ResultR row={r} onSaved={load} />
                </div>
                <div className="text-sm text-slate-600 mt-1 grid grid-cols-3 gap-2 max-w-md">
                  {r.entry && <div><span className="text-slate-400 text-xs uppercase tracking-wider">Entry</span><div className="font-semibold">{r.entry}</div></div>}
                  {r.tp && <div><span className="text-slate-400 text-xs uppercase tracking-wider">TP</span><div className="font-semibold text-emerald-700">{r.tp}</div></div>}
                  {r.sl && <div><span className="text-slate-400 text-xs uppercase tracking-wider">SL</span><div className="font-semibold text-rose-700">{r.sl}</div></div>}
                </div>
                {r.thesis && <div className="text-sm text-slate-600 mt-2 line-clamp-2 max-w-2xl">{r.thesis}</div>}
                <CardLinks id={r.id} cards={r.card_urls} />
                <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                  <TierBadge
                    posted={!!r.premium_posted_at}
                    scheduled={false}
                    label={r.premium_posted_at ? `Premium · ${fmtDate(r.premium_posted_at)}` : "Premium"}
                    color="bg-amber-50 text-amber-800 border border-amber-200"
                  />
                  <TierBadge
                    posted={!!r.basic_posted_at}
                    scheduled={!r.basic_posted_at}
                    label={r.basic_posted_at ? `Basic · ${fmtDate(r.basic_posted_at)}` : `Basic · ${fmtDate(r.basic_scheduled_at)}`}
                    color="bg-blue-50 text-blue-800 border border-blue-200"
                  />
                  <TierBadge
                    posted={!!r.free_released_at}
                    scheduled={false}
                    label={r.free_released_at ? `Free · ${fmtDate(r.free_released_at)}` : "Free · not released"}
                    color="bg-emerald-50 text-emerald-800 border border-emerald-200"
                  />
                </div>
                {/* ES mirror row — compact language-tagged status pills */}
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-0.5">ES</span>
                  <TierBadge
                    posted={!!r.premium_posted_at_es}
                    scheduled={false}
                    label={r.premium_posted_at_es ? `Premium · ${fmtDate(r.premium_posted_at_es)}` : "Premium · pending"}
                    color="bg-amber-50/60 text-amber-700 border border-amber-100"
                  />
                  <TierBadge
                    posted={!!r.basic_posted_at_es}
                    scheduled={!r.basic_posted_at_es}
                    label={r.basic_posted_at_es ? `Basic · ${fmtDate(r.basic_posted_at_es)}` : "Basic · pending"}
                    color="bg-blue-50/60 text-blue-700 border border-blue-100"
                  />
                  <TierBadge
                    posted={!!r.free_released_at_es}
                    scheduled={false}
                    label={r.free_released_at_es ? `Free · ${fmtDate(r.free_released_at_es)}` : "Free · pending"}
                    color="bg-emerald-50/60 text-emerald-700 border border-emerald-100"
                  />
                </div>
                {r.last_error && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                    <AlertTriangle size={12} /> {r.last_error}
                  </div>
                )}
                {r.last_error_es && (
                  <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                    <AlertTriangle size={12} /> ES: {r.last_error_es}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {r.premium_posted_at && r.status === "open" && (
                  <Button
                    size="sm"
                    onClick={() => openUpdateModal(r)}
                    className="gap-2 bg-amber-500 hover:bg-amber-400 text-white"
                    data-testid={`admin-trade-setups-update-${r.id}`}
                  >
                    <Send size={13} /> Post Update
                  </Button>
                )}
                {!r.free_released_at && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => releaseFree(r.id)}
                    className="gap-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    data-testid={`admin-trade-setups-release-free-${r.id}`}
                  >
                    <Users size={13} /> Release to Free
                  </Button>
                )}
                {!r.basic_posted_at && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => repostBasicNow(r.id)}
                    className="gap-2 border-blue-300 text-blue-800 hover:bg-blue-50"
                    data-testid={`admin-trade-setups-basic-now-${r.id}`}
                  >
                    <Send size={13} /> Post to Basic now
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(r.id)}
                  className="gap-2 text-slate-500 hover:text-rose-700"
                  data-testid={`admin-trade-setups-delete-${r.id}`}
                >
                  <Trash2 size={13} /> Delete
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Post Update modal — fires to every existing thread for the setup */}
      {updateFor && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeUpdateModal}
          data-testid="admin-trade-setups-update-modal"
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600 mb-1">
                  Post Update
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {updateFor.symbol} · {dirLabel(updateFor.direction)}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Fans out to every thread that exists · auto-translated for ES threads
                </div>
              </div>
              <button
                onClick={closeUpdateModal}
                className="text-slate-400 hover:text-slate-700 text-xl leading-none"
                data-testid="admin-trade-setups-update-close"
              >
                ×
              </button>
            </div>

            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Update note
            </label>
            <Textarea
              value={updateNote}
              onChange={(e) => setUpdateNote(e.target.value)}
              placeholder="e.g. Moved SL to breakeven · trailing 20 pips"
              rows={4}
              className="mb-4"
              data-testid="admin-trade-setups-update-note"
            />

            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Status
            </label>
            <div className="grid grid-cols-5 gap-2 mb-4">
              {[
                { v: "open",        l: "Keep open",   c: "border-slate-300 text-slate-700" },
                { v: "target_hit",  l: "✅ Target",   c: "border-emerald-300 text-emerald-800 hover:bg-emerald-50" },
                { v: "stopped",     l: "❌ Stopped",  c: "border-rose-300 text-rose-800 hover:bg-rose-50" },
                { v: "breakeven",   l: "➖ BE",        c: "border-slate-300 text-slate-700 hover:bg-slate-50" },
                { v: "invalidated", l: "⏸ Invalid",  c: "border-slate-300 text-slate-600 hover:bg-slate-50" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setUpdateStatus(opt.v)}
                  className={`h-9 rounded-md border text-xs font-semibold transition-colors ${opt.c} ${updateStatus === opt.v ? "ring-2 ring-amber-400 bg-amber-50" : ""}`}
                  data-testid={`admin-trade-setups-update-status-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>

            {R_STATUSES.includes(updateStatus) && (
              <label className="block text-xs font-semibold text-slate-700 mb-6">
                Result R (optional). Leave blank if you don't want R counted{updateStatus === "breakeven" ? ". Breakeven defaults to 0R" : ""}.
                <Input data-testid="admin-trade-setups-update-rr" className="mt-1.5 w-40" placeholder="+3.5, -1, 1:3.5"
                  value={updateRR} onChange={(e) => setUpdateRR(e.target.value)} />
              </label>
            )}
            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={closeUpdateModal}
                disabled={updateSending}
                data-testid="admin-trade-setups-update-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submitUpdate}
                disabled={updateSending || !updateNote.trim()}
                className="bg-amber-500 hover:bg-amber-400 text-white gap-2"
                data-testid="admin-trade-setups-update-submit"
              >
                <Send size={13} /> {updateSending ? "Posting…" : "Post to threads"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
